import { useEffect, useState } from 'react';

import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import { Progress } from 'antd';
import _ from 'lodash';
import { Color } from 'src/styles';
import zxcvbn from 'zxcvbn';

import { MTypography } from '../MTypography/MTypography';

import * as Styles from './PasswordStrengthBar.styles';

const InvalidLogo = () => <CloseOutlined style={{ fontSize: 11, color: Color.Red5 }} />;

const ValidLogo = () => <CheckOutlined style={{ fontSize: 11, color: Color.Green6 }} />;

const renderPasswordRule = (isValid: boolean, message: string) => (
  <>
    <MTypography variant='body1' inline color={isValid ? Color.Gray8 : Color.Red6}>
      {isValid ? <ValidLogo /> : <InvalidLogo />} {message}
    </MTypography>
    <br />
  </>
);

export const calculatePercentage = (value: number) => {
  switch (value) {
    case 0:
      return 10;
    case 1:
      return 25;
    case 2:
      return 50;
    case 3:
      return 80;
    case 4:
      return 100;
    default:
      return 0;
  }
};

export const calculateBarColor = (value: number) => {
  switch (value) {
    case 0:
      return Color.Red6;
    case 1:
      return Color.Red4;
    case 2:
      return Color.Orange6;
    case 3:
      return Color.Green5;
    case 4:
      return Color.Green6;
    default:
      return Color.Red6;
  }
};

export const calculateLabel = (value: number) => {
  switch (value) {
    case 0:
      return 'Very Weak';
    case 1:
      return 'Weak';
    case 2:
      return 'Average';
    case 3:
      return 'Strong';
    case 4:
      return 'Very Strong';
    default:
      return 'Very Weak';
  }
};

interface PasswordStrengthBarProps {
  password?: string;
  confirmPassword?: string;
  firstName?: string;
  lastName?: string;
  showStrengthLabel?: boolean;
  showValidationRules?: boolean;
}

export const PasswordStrengthBar = ({
  password = '',
  confirmPassword = '',
  firstName = '',
  lastName = '',
  showStrengthLabel = false,
  showValidationRules = false,
}: PasswordStrengthBarProps) => {
  const [result, setResult] = useState(0);
  const [testedResult, setTestedResult] = useState(0);
  const [color, setColor] = useState('#F25F5C');
  const [label, setLabel] = useState('');

  useEffect(() => {
    setTestedResult(zxcvbn(password).score);
    setResult(calculatePercentage(testedResult));
    setLabel(calculateLabel(testedResult));
    setColor(calculateBarColor(testedResult));
  }, [password, testedResult]);

  return (
    <div style={{}}>
      {testedResult >= 0 && !_.isEmpty(password) && (
        <>
          <Progress percent={_.isEmpty(password) ? 0 : result} strokeColor={color} showInfo={false} />
          <div style={{ display: 'flex', marginTop: -5 }}>
            {showStrengthLabel && !_.isEmpty(password) && (
              <MTypography variant='caption' inline color={color} className={Styles.strengthLabel}>
                {label}
              </MTypography>
            )}
          </div>
          {showValidationRules && !_.isEmpty(password) && (
            <div>
              {renderPasswordRule(password.length >= 8, 'Be 8 character’s at minimum')}
              {renderPasswordRule(/[A-Z]/.test(password), 'Contain one uppercase letter')}
              {renderPasswordRule(/[a-z]/.test(password), 'Contain one lowercase letter')}
              {renderPasswordRule(/[\d]/.test(password), 'Contain one number')}
              {renderPasswordRule(
                !password?.toLowerCase().includes(firstName.toLowerCase()),
                'Password should not contain first name',
              )}
              {renderPasswordRule(
                !password?.toLowerCase().includes(lastName.toLowerCase()),
                'Password should not contain last name',
              )}
              {renderPasswordRule(
                !password?.toLowerCase().includes('password'),
                'Password should not contain the word "password"',
              )}
              {renderPasswordRule(confirmPassword === password, 'Confirm Password should be the same as password')}
              {renderPasswordRule(
                /[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(password),
                'Contain one special character',
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
